import { defineStore } from 'pinia';
import { useState } from '@/composables/common/useState';
import { MilestoneEntity } from '@/types/common/milestone.entity';
import { useToggle } from '@/composables/useToggle';
import { useToast } from '@/composables/useToast';
import AirHouseService from '@/services/air-imports/house.service';
import { AxiosError } from 'axios';
import { AirHblEntity } from '@/types/air/hbl/air-hbl.entity';
import { AirHblMutatePayload } from '@/types/air/hbl';
import i18n from '@/i18n';

export const useAirHblStore = defineStore('AirHBL', () => {
  const toast = useToast();

  const [isFetchingHbl, toggleHblLoading] = useToggle();
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [readonly, toggleReadonly] = useToggle(true);
  const [hbl, setHbl] = useState<AirHblEntity | null>(null);
  const [milestones, setMilestones] = useState<MilestoneEntity[]>([]);
  const [isUpdatingHbl, toggleUpdatingHbl] = useToggle();

  const create = async (payload: AirHblMutatePayload) => {
    const { data: result } = await AirHouseService.create(payload);
    if (!result) {
      return { success: false, message: result.message || i18n.t('ServerErrorTryAgainLater') };
    }
    return { success: true, message: i18n.t('HBLCreatedSuccessfully'), data: result };
  };

  const update = async (payload: AirHblMutatePayload, hblId: string) => {
    toggleUpdatingHbl(true);
    const { data: result } = await AirHouseService.update(payload, hblId);
    if (!result) {
      return { success: false, message: result.message || i18n.t('ServerErrorTryAgainLater') };
    }
    await fetchHblById(hblId);
    toggleUpdatingHbl(false);

    return { success: true, message: i18n.t('HBLSavedSuccessfully') };
  };

  const deleteHbl = async (id: string) => {
    try {
      await AirHouseService.delete(id);
    } catch (error) {
      toast.error((error as AxiosError).response?.data as string);
      throw error;
    }
  };

  const fetchHblById = async (id: string) => {
    try {
      toggleHblLoading(true);
      const { data } = await AirHouseService.get(id);
      setHbl(data);
    } catch (e) {
      console.log('fetchHblById', e);
      throw e;
    } finally {
      toggleHblLoading(false);
    }
  };

  const fetchMilestones = async (id: string) => {
    try {
      const { data } = await AirHouseService.getMilestones(id);
      setMilestones(data);
    } catch (e) {
      console.log('fetchMilestones', e);
    }
  };

  // const validate = async (id: string) => {
  //   try {
  //     const { data } = await HouseService.validateStatus(id);
  //     if (data.hblStatus) toast.success('HBL is valid!');
  //     else {
  //       let message =
  //         'HBL is not valid! <br /> Discrepancy between Received and Manifested values: <br />';
  //       if (!data.isPcsValid) message = message.concat('PCS, ');
  //       if (!data.isWeightValid) message = message.concat('Weight, ');
  //       if (!data.isVolumeValid) message = message.concat('Volume');
  //       toast.error(message);
  //     }
  //     return data;
  //   } catch (e) {
  //     console.log(e);
  //   }
  // };

  return {
    hbl,
    milestones,
    isUpdatingHbl,

    hasChanges,
    setHasChanges,

    readonly,
    toggleReadonly,

    isFetchingHbl,

    setHbl,
    create,
    update,
    deleteHbl,
    fetchHblById,
    fetchMilestones,
    // validate,
  };
});
